import React, { useEffect, useContext, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from 'Context';
import { Pathname } from 'Routes';
import VerificationLoader from '../../../../Components/VerificationLoader';
import { AuthPage } from '../components/page';

function AutoLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { forceAutoSignin, setToken, getToken, removeToken } = useContext(AuthContext);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const hasAttempted = useRef(false);

  const routed_token = searchParams.get('token');
  const routed_path = searchParams.get('path');

  // Reconstruct full path with any additional query params
  const getFullPath = () => {
    if (!routed_path) return null;

    // Ensure path starts with '/' for absolute navigation
    const normalizedPath = routed_path.startsWith('/') ? routed_path : `/${routed_path}`;

    // Get all params except 'token' and 'path'
    const additionalParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
      if (key !== 'token' && key !== 'path') {
        additionalParams.append(key, value);
      }
    });

    // If there are additional params, append them to the path
    const additionalParamsString = additionalParams.toString();
    if (additionalParamsString) {
      const separator = normalizedPath.includes('?') ? '&' : '?';
      return `${normalizedPath}${separator}${additionalParamsString}`;
    }

    return normalizedPath;
  };

  const fullPath = getFullPath();

  // Landing on /autoLogin implies a token-based login from an external link
  // — drop any leftover manager-impersonation markers so the previous
  // session's "Accessed X" banner doesn't carry over into the auto-login
  // target page. Empty deps → runs once on mount.
  useEffect(() => {
    localStorage.removeItem('isAccessingUser');
    localStorage.removeItem('accessedUserId');
  }, []);

  useEffect(() => {
    const performAutoLogin = async () => {
      // Prevent multiple attempts or loops
      if (hasAttempted.current) {
        return;
      }

      // Check if this token has already failed - redirect to path immediately
      const failedToken = sessionStorage.getItem('failedAutoLoginToken');
      if (failedToken === routed_token && fullPath) {
        sessionStorage.removeItem('failedAutoLoginToken');
        navigate(fullPath, { replace: true });
        return;
      }

      if (!routed_token || !fullPath) {
        // No token or path, redirect to homepage
        navigate(Pathname.home, { replace: true });
        return;
      }

      // Mark that we've attempted authentication
      hasAttempted.current = true;

      try {
        // Clear any existing tokens first to prevent conflicts
        removeToken();

        // Store token in localStorage
        localStorage.setItem('token', routed_token);
        localStorage.setItem('dismissedPaymentDialog', 'true');

        // Set token in context
        setToken(routed_token);

        // Give a brief moment for token to be set
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Trigger auto sign-in to fetch user profile
        forceAutoSignin();

        // Wait a bit for the profile to load
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Verify token is still valid
        const currentToken = getToken();
        if (!currentToken) {
          throw new Error('Authentication failed - token not found after sign in');
        }

        // Clear the failed token marker if authentication succeeded
        sessionStorage.removeItem('failedAutoLoginToken');

        // Navigate to the intended path with query params (logged in)
        navigate(fullPath, { replace: true });
      } catch (error) {
        console.error('AutoLogin error:', error);

        // Mark this token as failed to prevent loops
        sessionStorage.setItem('failedAutoLoginToken', routed_token);

        // Clear the invalid token
        removeToken();
        localStorage.removeItem('token');

        // Redirect to desired path anyway (user will be logged out)
        navigate(fullPath, { replace: true });
      } finally {
        setIsAuthenticating(false);
      }
    };

    performAutoLogin();
  }, [routed_token, fullPath, setToken, forceAutoSignin, getToken, removeToken, navigate]);

  return (
    <div>
      <AuthPage>
        <VerificationLoader
          isVerified={false}
          verificationMessage={{ type: 'waiting', message: 'Signing you in...' }}
          verifyEmail={() => {}}
          isLoading={isAuthenticating}
          from="web"
        />
      </AuthPage>
    </div>
  );
}

export default AutoLogin;
